import React, { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import { PencilSimpleLine } from '@phosphor-icons/react'
import AppChip from '../../../components/chips/AppChip'
import FormHeaderDetailActions from '../../../components/action_button/FormHeaderDetailActions'
import FormHeaderSaveActions from '../../../components/action_button/FormHeaderSaveActions'
import {
  UI_COLOR_BORDER_LIGHT,
  UI_COLOR_PRIMARY
} from '../../../constants/uiColors'
import {
  PROFILE_INFO_COPY,
  profileInfoCardSx,
  profileInfoContentGridSx,
  profileInfoHeaderSx,
  profileInfoSummaryDividerSx,
  profileInfoSummaryLabelSx,
  profileInfoSummaryRowSx,
  profileInfoTitleSx
} from './constants/profileInfoConstants'
import {
  buildProfileInfoForm,
  buildProfileRoleChipSx,
  buildProfileStatusChipSx,
  getProfileInfoStatusMeta,
  getProfilePrimaryRole,
  isProfileInfoDirty
} from './helpers/profileInfoHelpers'
import ProfileInfoDetailBlock from './components/ProfileInfoDetailBlock'
import ProfileInfoReadValue from './components/ProfileInfoReadValue'
import ProfileInfoStyledField from './components/ProfileInfoStyledField'
import { updateMyProfile } from '../services/profileApi'

export default function ProfileInfoSection({ profile, onProfileChange, onActionAlert }) {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState(buildProfileInfoForm(profile))

  useEffect(() => {
    if (!editing) {
      setForm(buildProfileInfoForm(profile))
    }
  }, [profile, editing])

  const primaryRole = getProfilePrimaryRole(profile)
  const currentStatus = getProfileInfoStatusMeta(profile)
  const dirty = useMemo(() => isProfileInfoDirty(form, profile), [form, profile])

  const handleCancel = () => {
    setEditing(false)
    setForm(buildProfileInfoForm(profile))
  }

  const handleSave = async () => {
    if (!form.name.trim()) {
      onActionAlert?.(PROFILE_INFO_COPY.nameRequired, 'warning')
      return
    }

    setBusy(true)
    try {
      const updatedProfile = await updateMyProfile({
        name: form.name.trim(),
        phone: form.phone.trim()
      })
      onProfileChange?.(updatedProfile)
      setEditing(false)
      onActionAlert?.(PROFILE_INFO_COPY.updateSuccess)
    } catch (error) {
      onActionAlert?.(error.message || PROFILE_INFO_COPY.updateError, 'warning')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box>
      <Box sx={profileInfoCardSx}>
        <Box sx={profileInfoHeaderSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <PersonOutlineRoundedIcon sx={{ fontSize: 21, color: UI_COLOR_PRIMARY }} />
            <Typography sx={profileInfoTitleSx}>{PROFILE_INFO_COPY.title}</Typography>
          </Box>

          {editing ? (
            <FormHeaderSaveActions
              onCancel={handleCancel}
              onPrimaryClick={handleSave}
              primaryButtonType="button"
              isSaving={busy}
              primaryDisabled={!dirty}
            />
          ) : (
            <FormHeaderDetailActions
              showSecondary={false}
              primaryLabel="Editar"
              primaryIcon={PencilSimpleLine}
              onPrimary={() => setEditing(true)}
            />
          )}
        </Box>

        <Divider sx={{ borderColor: UI_COLOR_BORDER_LIGHT }} />

        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 2, sm: 2.5 } }}>
          <Box sx={profileInfoSummaryRowSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={profileInfoSummaryLabelSx}>Estado</Typography>
              <AppChip label={currentStatus.label} renderAs="span" sx={buildProfileStatusChipSx(profile)} />
            </Box>

            <Box sx={profileInfoSummaryDividerSx} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={profileInfoSummaryLabelSx}>Rol</Typography>
              <AppChip label={primaryRole} renderAs="span" sx={buildProfileRoleChipSx()} />
            </Box>
          </Box>

          <Box sx={profileInfoContentGridSx}>
            <ProfileInfoDetailBlock label="Código">
              <ProfileInfoReadValue value={profile?.code} />
            </ProfileInfoDetailBlock>

            <ProfileInfoDetailBlock label="Nombre completo">
              {editing ? (
                <ProfileInfoStyledField
                  label="Nombre"
                  value={form.name}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                  disabled={busy}
                  placeholder="Ingresa tu nombre"
                />
              ) : (
                <ProfileInfoReadValue value={profile?.name} />
              )}
            </ProfileInfoDetailBlock>

            <ProfileInfoDetailBlock label="Correo electrónico">
              <ProfileInfoReadValue value={profile?.email} />
            </ProfileInfoDetailBlock>

            <ProfileInfoDetailBlock label="Negocio">
              <ProfileInfoReadValue value={profile?.business} />
            </ProfileInfoDetailBlock>

            <ProfileInfoDetailBlock label="Teléfono">
              {editing ? (
                <ProfileInfoStyledField
                  label="Teléfono móvil"
                  value={form.phone}
                  onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
                  disabled={busy}
                  placeholder="Ej. 6000 0000"
                  inputProps={{ inputMode: 'tel' }}
                />
              ) : (
                <ProfileInfoReadValue value={profile?.phone} />
              )}
            </ProfileInfoDetailBlock>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
